// Keeps this device and the account in step. Cheap by design: nothing is sent unless something changed, and a
// quiet pull runs once a minute while the page is open so a second device catches up on its own.

import { useSyncExternalStore } from "react";
import { api, ApiError } from "./api";
import * as store from "./store";

export type SyncState = "off" | "idle" | "syncing" | "offline" | "error";

let state: SyncState = "off";
let lastOk = 0;
let snapshot: { state: SyncState; lastOk: number } = { state, lastOk };
const listeners = new Set<() => void>();
function setState(s: SyncState, ok = lastOk): void {
  state = s;
  lastOk = ok;
  snapshot = { state, lastOk };
  listeners.forEach((l) => l());
}
export function useSyncStatus(): { state: SyncState; lastOk: number } {
  return useSyncExternalStore((fn) => (listeners.add(fn), () => listeners.delete(fn)), () => snapshot, () => snapshot);
}

let userId: string | null = null;
let running = false;
let queued = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let resendStreak = 0;

const hasDirty = (): boolean => {
  const d = store.get();
  return d.dirtyProgress.length > 0 || d.dirtyDays.length > 0 || d.settingsDirty;
};

export function syncSoon(ms = 3500): void {
  if (!userId || timer) return;
  timer = setTimeout(() => {
    timer = null;
    void syncNow();
  }, ms);
}

export async function syncNow(opts: { keepalive?: boolean } = {}): Promise<boolean> {
  if (!userId) return false;
  if (running) {
    queued = true;
    return false;
  }
  const d = store.get();
  if (d.owner !== userId) return false; // the account hasn't been adopted on this device yet
  running = true;
  setState("syncing");
  try {
    const payload = store.dirtyPayload();
    const res = await api.sync(
      { device: d.device, since: Math.max(0, d.lastSync - 60_000), progress: payload.progress, days: payload.days, settings: payload.settings },
      opts.keepalive
    );
    const { resend } = store.applySync({ progress: payload.progress, days: payload.days, settingsAt: payload.settings?.at ?? null }, res, d.owner);
    setState("idle", Date.now());
    resendStreak = resend ? resendStreak + 1 : 0;
    if (resend && resendStreak <= 3) syncSoon(5000 * resendStreak);
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) setState("offline");
    else if (e instanceof ApiError && e.status === 401) setState("off");
    else setState("error");
    return false;
  } finally {
    running = false;
    if (queued) {
      queued = false;
      syncSoon(800);
    }
  }
}

/** call with the signed-in user's id (or null when signed out) */
export function setSyncUser(id: string | null): void {
  userId = id;
  if (!id) {
    setState("off");
    return;
  }
  setState("idle");
  void syncNow();
}

// changes made on this device go out a few seconds later
store.subscribe(() => {
  if (userId && hasDirty()) syncSoon();
});

if (typeof window !== "undefined") {
  window.addEventListener("online", () => void syncNow());
  document.addEventListener("visibilitychange", () => {
    if (!userId) return;
    void syncNow({ keepalive: document.visibilityState === "hidden" });
  });
  setInterval(() => {
    if (userId && document.visibilityState === "visible") void syncNow();
  }, 60_000);
}
